import styled from "@emotion/styled";
import {
  Box,
  Button,
  DialogProps,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { Controller } from "react-hook-form";
import useAuth from "../../../hooks/useAuth";

// import MuiTextField from "../../components/inputs/MuiTextField";
import BasicModal from "../../components/modal/BasicModal";
import useCases from "../../../hooks/useCases";
import { useSnackbarHelper } from "../../components/Snackbar";
import { uploadDpsToS3 } from "../../components/common/uploadDpsToS3";
import { stripS3Prefix } from "../../../utils/s3";
// import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

function UploadDpsReport({ onClose, caseData, ...props }: any) {
  const showSnackbar = useSnackbarHelper();
  const { request } = useAuth();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [removeFile, setRemoveFile] = useState<boolean>(true);

  const [isStaticLoading, setStaticLoading] = useState(false);

  const { uploadDps, isUploadingDps, refetchCase } = useCases(caseData?._id);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    setFile(selectedFile);
  };

  const MAX_FILE_SIZE_MB = 5; // 5 MB limit

  const upload = async () => {
    try {
      setStaticLoading(true);

      if (!file) {
        setStaticLoading(false);

        return;
      }

      const fileSizeMB = file.size / (1024 * 1024); // Bytes → MB

      if (fileSizeMB > MAX_FILE_SIZE_MB) {
        const message = `File size exceeds ${MAX_FILE_SIZE_MB} MB limit.`;
        showSnackbar(message, "error");
        setStaticLoading(false);

        return;
      }

      const data = {
        file: file,
        caseId: caseData?.caseId,
        inmateName: caseData?.contactId?.inmate?.name || "county_inmate",
        request,
      };
      const result = await uploadDpsToS3(data);

      const payload = {
        dpsUrl: stripS3Prefix(result?.Location),
        fileName: file?.name,
        fileType: file?.type,
      };

      const respone = await uploadDps(payload);

      const message = respone?.message;
      showSnackbar(message, "success");

      setStaticLoading(false);
      // refetchCase();
      onClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || "An error occurred";
      showSnackbar(message, "error");
    }
  };

  return (
    <BasicModal
      onClose={onClose}
      title="Upload DPS Report"
      isLoader={isUploadingDps || isStaticLoading}
      loadingTitle="Loading..."
      content={
        <Box>
          <Grid
            container
            spacing={2}
            sx={{
              "& .outlined .MuiOutlinedInput-notchedOutline": {
                border: "2px solid #AFD2F0 !important",
              },
            }}
          >
            {/* <Grid item xs={6}></Grid> */}
            <Grid item xs={12}>
              <Box>
                <Controller
                  name="document"
                  render={({ field }) => (
                    <>
                      <Button
                        variant="contained"
                        component="span"
                        sx={{ textTransform: "none", mb: 1 }}
                        onClick={() => {
                          fileInputRef.current?.click();
                          if (!file && !removeFile) setRemoveFile(true);
                        }}
                      >
                        Upload Document
                      </Button>

                      <input
                        {...field}
                        type="file"
                        ref={fileInputRef}
                        accept="application/pdf"
                        style={{ display: "none" }}
                        onChange={handleFileSelect}
                      />

                      {file && (
                        <Typography variant="body2">
                          Selected file: {file.name}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      }
      actions={
        <>
          <Button
            color="error"
            variant="outlined"
            onClick={onClose}
            sx={{ textTransform: "none" }}
            disabled={isUploadingDps}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="success"
            sx={{ textTransform: "capitalize" }}
            onClick={upload}
            disabled={!file || isUploadingDps || isStaticLoading}
          >
            Upload
          </Button>
        </>
      }
      {...props}
    />
  );
}

export default UploadDpsReport;
